<template>
  <section v-if="pending" :class="$style.placeholder">
    <ui-spinner :class="$style.spinner" />
  </section>

  <section v-else-if="error" :class="$style.placeholder">
    <ui-icon name="x" :class="$style.icon" />
    <h2 :class="$style.title">{{ t('error.title') }}</h2>
    <p :class="$style.subtitle">{{ t('error.text') }}</p>
  </section>

  <swiss-board
    v-else-if="swissStage"
    :stage="swissStage"
    :compact-mode="compactMode"
    :show-unlock-banner="isShowUnlockBanner"
  />

  <bracket-board
    v-else-if="bracketStage"
    :stage="bracketStage"
    :show-unlock-banner="isShowUnlockBanner"
  />

  <section v-else :class="$style.placeholder">
    <ui-icon name="bracket" :class="$style.icon" />
    <h2 :class="$style.title">{{ summary.name }}</h2>
    <p :class="$style.subtitle">{{ summarySubtitle }}</p>
  </section>
</template>

<i18n lang="yaml">
ru:
  error:
    title: Не удалось загрузить этап
    text: Попробуйте обновить страницу. Если ошибка повторится — попозже.
  status:
    locked: Заблокирован
    awaiting_open: Скоро откроется
    awaiting_results: Ждём результаты
    finished: Этап завершён
    pickable: Открыт для пиков
</i18n>

<script lang="ts" setup>
import { computed, watch } from 'vue'
import { storeToRefs } from 'pinia'
import UiIcon from '@/ui-core/components/icon/index.vue'
import UiSpinner from '@/ui-core/components/spinner/index.vue'
import { useAccountStore } from '~/stores/account'
import SwissBoard from './swiss/SwissBoard.vue'
import BracketBoard from './bracket/BracketBoard.vue'
import { useStageStore } from '../../stores/stage'
import { useUnlockStore } from '../../stores/unlock'
import { useTournamentStore } from '../../stores/tournament'
import {
  usePickemAnalytics,
  type PickemStageSourceBlock
} from '../../composables/use-pickem-analytics'
import type { Stage, StageSummary, StageUiState } from '../../types'

defineOptions({ name: 'PickemStageView' })

const props = defineProps<{
  stage: StageSummary
  stageSourceBlock?: PickemStageSourceBlock
  compactMode?: boolean
}>()

const { t } = useI18n()
const accountStore = useAccountStore()
const { isAuthorized } = storeToRefs(accountStore)
const stageStore = useStageStore()
const tournamentStore = useTournamentStore()
const { trackUnlockScreenView } = usePickemAnalytics()
// `data` из store — полный Stage. Алиасим как `fullStage`, чтобы не конфликтовать
// с пропом `stage` (StageSummary).
const { data: fullStage } = storeToRefs(stageStore)

// useAsyncData реагирует на смену id и флага авторизации — перезапрашивает Stage и кладёт
// результат в store через fetchItem. pending/error берём отсюда.
// Ключ должен учитывать id, иначе nuxt считает запрос идентичным предыдущему;
// делаем это через computed внутри generic-ключа Nuxt.
const { pending, error } = useAsyncData(
  `pickem-stage-${props.stage.id}`,
  () => stageStore.fetchItem(props.stage.id),
  { watch: [() => props.stage.id, isAuthorized] }
)

const UI_STATES_WITH_SHOWING_UNLOCK_BANNER: StageUiState[] = [
  'locked',
  'awaiting_open'
]

const isShowUnlockBanner = computed(() =>
  Boolean(
    fullStage.value &&
      UI_STATES_WITH_SHOWING_UNLOCK_BANNER.includes(fullStage.value.ui_state) &&
      fullStage.value.unlock.required &&
      !fullStage.value.unlock.user_unlocked
  )
)

const lockedStage = computed(() => {
  if (!fullStage.value || tournamentStore.isFinished) return null
  if (!fullStage.value.unlock?.required) return null
  if (fullStage.value.unlock.user_unlocked) return null
  return fullStage.value
})

// Подписываем стор разблокировки на текущий этап. Удобно делать это
// здесь (а не в UnlockBanner), чтобы стор знал актуальный snapshot
// сразу при заходе на этап — даже если баннер ещё не смонтирован.
const unlockStore = useUnlockStore()
watch(
  () => fullStage.value,
  next => {
    if (next?.unlock) unlockStore.setLocal(next.id, next.unlock)
  },
  { immediate: true }
)

watch(
  lockedStage,
  stage => {
    const tournament = tournamentStore.tournament
    if (!stage || !tournament) return

    trackUnlockScreenView({
      stage,
      tournament,
      sourceBlock: props.stageSourceBlock ?? 'stage_carousel'
    })
  },
  { immediate: true }
)

const swissStage = computed(() => {
  if (!fullStage.value) return null
  if (fullStage.value.type !== 'swiss') return null
  if (!fullStage.value.swiss) return null
  return fullStage.value as Stage & { swiss: NonNullable<Stage['swiss']> }
})

const bracketStage = computed(() => {
  if (!fullStage.value) return null
  if (fullStage.value.type !== 'bracket') return null
  if (!fullStage.value.bracket) return null
  return fullStage.value as Stage & { bracket: NonNullable<Stage['bracket']> }
})

const summary = computed(() => props.stage)

const summarySubtitle = computed(() => {
  switch (props.stage.status) {
    case 'finished':
      return t('status.finished')
    default:
      if (props.stage.unlock_required && !props.stage.user_unlocked) {
        return t('status.locked')
      }
      return t('status.pickable')
  }
})
</script>

<style lang="scss" module>
.placeholder {
  padding: 4em 2em;
  text-align: center;
  border: 1px dashed color-get(--root-rgb-bg-border);
  border-radius: 1.2em;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1em;
}

.icon {
  width: 4em;
  height: 4em;
  color: color-get(--root-rgb-text-3);
}

.spinner {
  width: 4em;
  height: 4em;
}

.title {
  margin: 0;
  @include typo-get(headline-m);
  color: color-get(--root-rgb-text-1);
}

.subtitle {
  margin: 0;
  @include typo-get(subhead-s);
  color: color-get(--root-rgb-text-2);
}

.note {
  margin: 0;
  @include typo-get(body-s);
  color: color-get(--root-rgb-text-3);
  max-width: 32em;
}
</style>
